import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, money, fmtDateTime } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { NotesCard, LineItemsView, SatelliteView } from "@/components/DetailBits";
import PhotoGallery from "@/components/PhotoGallery";
import DocumentDialog from "@/components/DocumentDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, PencilSimple, Receipt, MapPin, CalendarBlank, User, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const FLOW = ["unscheduled", "scheduled", "in_progress", "completed", "cancelled"];

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [job, setJob] = useState(null);
  const [edit, setEdit] = useState(false);

  const load = () => api.getJob(id).then(setJob).catch(() => toast.error("Job not found"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!job) return <div className="p-10 text-slate-400">Loading job…</div>;

  const setStatus = async (s) => { await api.setJobStatus(id, s); toast.success(`Job ${s.replace("_", " ")}`); load(); };
  const makeInvoice = async () => { const inv = await api.invoiceFromJob(id); toast.success(`Invoice ${inv.number} created`); navigate("/invoices"); };

  const statusOptions = isAdmin ? FLOW : ["in_progress", "completed"];

  return (
    <div className="p-8 max-w-[1200px]">
      <button onClick={() => navigate("/jobs")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4 transition-colors duration-200" data-testid="back-to-jobs"><ArrowLeft size={16} /> Back to Jobs</button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm font-bold text-[#00B4D8]">{job.number}</span>
            <StatusBadge status={job.status} />
          </div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight mt-1">{job.title}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusOptions.includes(job.status) ? job.status : undefined} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="detail-job-status"><SelectValue placeholder="Set status" /></SelectTrigger>
            <SelectContent>{statusOptions.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
          {isAdmin && <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-job-button"><PencilSimple size={16} className="mr-1.5" /> Edit</Button>}
          {isAdmin && <Button onClick={makeInvoice} className="bg-[#0B1726] hover:bg-slate-800" data-testid="detail-generate-invoice"><Receipt size={16} className="mr-1.5" /> Invoice</Button>}
        </div>
      </div>

      {/* Info bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <InfoTile icon={User} label="Client" value={<Link to={`/clients/${job.client_id}`} className="text-[#00B4D8] hover:underline">{job.client_name}</Link>} />
        <InfoTile icon={MapPin} label="Property" value={job.property_address || "—"} />
        <InfoTile icon={CalendarBlank} label="Scheduled" value={job.scheduled_date ? `${job.scheduled_date} ${job.scheduled_time || ""}` : "Not scheduled"} />
      </div>

      {job.assigned_crew?.length > 0 && (
        <div className="mb-6 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Crew:</span>
          {job.assigned_crew.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full pl-1 pr-3 py-1 text-sm">
              <span className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold overflow-hidden">{c.avatar_url ? <img src={c.avatar_url} alt="" className="w-full h-full object-cover" /> : c.name[0]}</span>{c.name}
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <LineItemsView items={job.line_items} subtotal={job.subtotal} tax={job.tax} taxRate={job.tax_rate} total={job.total} doc={job} />
          <PhotoGallery jobId={id} />
        </div>
        <div className="space-y-6">
          {job.property_address && (
            <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500"><MapPin size={13} weight="duotone" className="text-[#00B4D8]" /> Site Location</div>
              <div className="text-sm font-medium text-slate-800 mt-1">{job.property_address}</div>
              <SatelliteView address={job.property_address} height={170} />
            </div>
          )}
          <NotesCard value={job.notes} onSave={(t) => api.patchJobNotes(id, t)} />
        </div>
      </div>

      <DocumentDialog open={edit} onClose={() => setEdit(false)} type="job" initial={job} onSaved={load} />
    </div>
  );
}

function InfoTile({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1"><Icon size={13} weight="duotone" className="text-[#00B4D8]" /> {label}</div>
      <div className="text-sm font-medium text-slate-800">{value}</div>
    </div>
  );
}
